import type { AgentResponse } from '@monorepo/shared/schemas/agents'
import {
  CurrencyCodeSchema,
  INVOICE_LINE_ITEM_SOURCE_TYPE,
} from '@monorepo/shared/schemas/common'
import type { CreateInvoice } from '@monorepo/shared/schemas/invoices'
import { format } from 'date-fns'
import { Calculator, CalendarIcon, Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { agentService } from '@/features/agents/services/agentService'
import { invoicesService } from '@/features/invoices'
import { toast } from '@/lib/toast'
import { CustomerAutocomplete } from '@/shared/components/common/CustomerAutocomplete'

import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'

interface CreateInvoiceDialogProps {
  onInvoiceCreated?: () => void
  preselectedCustomerId?: string
  disabled?: boolean
}

interface LineItemState {
  id: string
  agent_id: string
  signal_id: string
  // Auto-populated (read-only)
  action_type: string
  signal_name: string
  // Editable
  quantity: number
  unit_price_dollars: number
}

const createDefaultLine = (id: string): LineItemState => ({
  id,
  agent_id: '',
  signal_id: '',
  action_type: '',
  signal_name: '',
  quantity: 1,
  unit_price_dollars: 0,
})

export function CreateInvoiceDialog({
  onInvoiceCreated,
  preselectedCustomerId,
  disabled = false,
}: CreateInvoiceDialogProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [agents, setAgents] = useState<{ id: string; name: string }[]>([])

  // Agent detail cache
  const agentCacheRef = useRef<Map<string, AgentResponse>>(new Map())

  // Form state
  const [customerId, setCustomerId] = useState(preselectedCustomerId || '')
  const [issueDate, setIssueDate] = useState<Date>(new Date())
  const [dueDate, setDueDate] = useState<Date>(() => {
    const date = new Date()
    date.setDate(date.getDate() + 30)
    return date
  })
  const [issueDateOpen, setIssueDateOpen] = useState(false)
  const [dueDateOpen, setDueDateOpen] = useState(false)
  const [currency, setCurrency] = useState('USD')
  const [taxRate, setTaxRate] = useState('0')
  const [discountDollars, setDiscountDollars] = useState('0')
  const [notes, setNotes] = useState('')

  // Line items
  const [lines, setLines] = useState<LineItemState[]>([createDefaultLine('1')])

  // Per-line agent details (for signal dropdowns)
  const [agentDetailsMap, setAgentDetailsMap] = useState<
    Map<string, AgentResponse>
  >(new Map())

  // Fetch agents on mount
  useEffect(() => {
    agentService
      .getAgents()
      .then(res => {
        setAgents((res.data || []).map(a => ({ id: a.id, name: a.name })))
      })
      .catch(() => {})
  }, [])

  const resetForm = () => {
    setCustomerId(preselectedCustomerId || '')
    setIssueDate(new Date())
    const defaultDueDate = new Date()
    defaultDueDate.setDate(defaultDueDate.getDate() + 30)
    setDueDate(defaultDueDate)
    setCurrency('USD')
    setTaxRate('0')
    setDiscountDollars('0')
    setNotes('')
    setLines([createDefaultLine('1')])
    setAgentDetailsMap(new Map())
    agentCacheRef.current.clear()
  }

  // Fetch agent details (with caching)
  const fetchAgentDetails = useCallback(async (agentId: string) => {
    const cached = agentCacheRef.current.get(agentId)
    if (cached) {
      setAgentDetailsMap(prev => new Map(prev).set(agentId, cached))
      return cached
    }
    try {
      const agent = await agentService.getAgent(agentId)
      agentCacheRef.current.set(agentId, agent)
      setAgentDetailsMap(prev => new Map(prev).set(agentId, agent))
      return agent
    } catch {
      return null
    }
  }, [])

  // Line item helpers
  const addLine = () => {
    const newId = (
      Math.max(...lines.map(l => Number.parseInt(l.id, 10))) + 1
    ).toString()
    setLines([...lines, createDefaultLine(newId)])
  }

  const removeLine = (id: string) => {
    if (lines.length > 1) {
      setLines(lines.filter(line => line.id !== id))
    }
  }

  const updateLine = (id: string, updates: Partial<LineItemState>) => {
    setLines(
      lines.map(line => (line.id === id ? { ...line, ...updates } : line))
    )
  }

  const handleAgentChange = async (lineId: string, agentId: string) => {
    // Reset signal-dependent fields
    updateLine(lineId, {
      agent_id: agentId,
      signal_id: '',
      action_type: '',
      signal_name: '',
    })
    if (agentId) {
      await fetchAgentDetails(agentId)
    }
  }

  const handleSignalChange = (lineId: string, signalId: string) => {
    const line = lines.find(l => l.id === lineId)
    if (!line?.agent_id) {
      return
    }

    const agentDetail = agentDetailsMap.get(line.agent_id)
    if (!agentDetail?.actions) {
      return
    }

    // Find the action whose billable_signal matches
    const matchingAction = agentDetail.actions.find(
      a => a.billable_signal?.signal_id === signalId
    )

    const actionType = matchingAction?.action_type || ''
    const signalName = matchingAction?.billable_signal?.signal_name || ''

    updateLine(lineId, {
      signal_id: signalId,
      action_type: actionType,
      signal_name: signalName,
    })
  }

  // Get signal options for a line item's selected agent
  const getSignalOptions = (agentId: string) => {
    const agentDetail = agentDetailsMap.get(agentId)
    if (!agentDetail?.actions) {
      return []
    }
    return agentDetail.actions
      .filter(a => a.billable_signal)
      .map(a => ({
        signal_id: a.billable_signal?.signal_id,
        signal_name: a.billable_signal?.signal_name,
      }))
  }

  // Get agent name from agents list
  const getAgentName = (agentId: string) => {
    return agents.find(a => a.id === agentId)?.name || ''
  }

  // Calculations
  const subtotal = lines.reduce(
    (sum, line) => sum + line.quantity * line.unit_price_dollars,
    0
  )
  const discount = Number.parseFloat(discountDollars) || 0
  const taxableAmount = subtotal - discount
  const taxAmount = taxableAmount * (Number.parseFloat(taxRate) / 100)
  const total = taxableAmount + taxAmount

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const handleSubmit = async () => {
    if (!customerId || !issueDate || !dueDate || lines.length === 0) {
      return
    }

    const validLines = lines.filter(line => line.agent_id && line.signal_id)
    if (validLines.length === 0) {
      toast.error('Add at least one line item with agent and signal')
      return
    }

    setLoading(true)
    try {
      const invoiceData: CreateInvoice = {
        customer_id: customerId,
        issue_date: issueDate.toISOString(),
        due_date: dueDate.toISOString(),
        currency: currency as CreateInvoice['currency'],
        tax_rate: Number.parseFloat(taxRate) / 100 || 0,
        notes: notes || undefined,
        line_items: validLines.map((line, i) => {
          const agentName = getAgentName(line.agent_id)
          const unitPriceCents = Math.round(line.unit_price_dollars * 100)
          const subtotalCents = Math.round(line.quantity * unitPriceCents)

          return {
            description: `${agentName} - ${line.signal_name}`,
            quantity: line.quantity,
            unit_price_cents: unitPriceCents,
            subtotal_cents: subtotalCents,
            discount_cents: 0,
            total_cents: subtotalCents,
            source_type: INVOICE_LINE_ITEM_SOURCE_TYPE.agent_action,
            agent_id: line.agent_id || undefined,
            signal_id: line.signal_id || undefined,
            included_units_used: 0,
            sort_order: i,
          }
        }),
      }

      await invoicesService.createInvoice(invoiceData)
      toast.success('Invoice created successfully')
      resetForm()
      setOpen(false)
      onInvoiceCreated?.()
    } catch {
      toast.error('Failed to create invoice')
    } finally {
      setLoading(false)
    }
  }

  const isFormValid =
    customerId &&
    issueDate &&
    dueDate &&
    lines.some(line => line.agent_id && line.signal_id)

  return (
    <Drawer
      open={open}
      onOpenChange={newOpen => {
        setOpen(newOpen)
        if (!newOpen) {
          resetForm()
        }
      }}
      direction='right'
    >
      <DrawerTrigger asChild>
        <Button className='gap-2' disabled={disabled}>
          <Plus className='w-4 h-4' />
          Create Invoice
        </Button>
      </DrawerTrigger>
      <DrawerContent className='h-screen w-[75vw] min-w-[900px] max-w-[1400px] ml-auto fixed inset-y-0 right-0 flex flex-col'>
        <DrawerHeader className='border-b flex-shrink-0 px-6 py-4'>
          <DrawerTitle>Create Invoice</DrawerTitle>
          <DrawerDescription>
            Generate an invoice from agent signals
          </DrawerDescription>
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='space-y-6 w-full max-w-none'>
            {/* Customer & Invoice Number */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='customer-select'>Customer *</Label>
                <CustomerAutocomplete
                  value={customerId}
                  onValueChange={id => setCustomerId(id)}
                  placeholder='Select a customer...'
                  disabled={!!preselectedCustomerId}
                />
              </div>

              <div className='space-y-2'>
                <Label>Invoice Number</Label>
                <div className='p-3 border rounded-md bg-muted/20 text-sm text-muted-foreground'>
                  Auto-generated by the system
                </div>
              </div>
            </div>

            {/* Dates & Currency */}
            <div className='grid grid-cols-3 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='issue-date'>Issue Date *</Label>
                <Popover open={issueDateOpen} onOpenChange={setIssueDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='outline'
                      className='w-full justify-between font-normal'
                    >
                      {issueDate ? format(issueDate, 'PPP') : 'Select date'}
                      <CalendarIcon className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className='w-auto overflow-hidden p-0'
                    align='start'
                  >
                    <Calendar
                      mode='single'
                      selected={issueDate}
                      captionLayout='dropdown'
                      onSelect={date => {
                        if (date) {
                          setIssueDate(date)
                          setIssueDateOpen(false)
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='due-date'>Due Date *</Label>
                <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='outline'
                      className='w-full justify-between font-normal'
                    >
                      {dueDate ? format(dueDate, 'PPP') : 'Select date'}
                      <CalendarIcon className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className='w-auto overflow-hidden p-0'
                    align='start'
                  >
                    <Calendar
                      mode='single'
                      selected={dueDate}
                      captionLayout='dropdown'
                      onSelect={date => {
                        if (date) {
                          setDueDate(date)
                          setDueDateOpen(false)
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='currency'>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CurrencyCodeSchema.options.map(code => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <Label className='text-lg font-medium'>Line Items</Label>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={addLine}
                >
                  <Plus className='w-4 h-4 mr-1' />
                  Add Line
                </Button>
              </div>

              <div className='border rounded-lg'>
                {/* Header */}
                <div className='grid grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr_auto] gap-2 p-3 bg-muted/50 text-xs font-medium'>
                  <div>Agent Name</div>
                  <div>Signal Name</div>
                  <div>Type</div>
                  <div>Qty</div>
                  <div>Price/Unit</div>
                  <div>Total</div>
                  <div className='w-8' />
                </div>

                {/* Rows */}
                {lines.map(line => {
                  const lineTotal = line.quantity * line.unit_price_dollars
                  const signalOptions = line.agent_id
                    ? getSignalOptions(line.agent_id)
                    : []

                  return (
                    <div
                      key={line.id}
                      className='grid grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr_auto] gap-2 p-3 border-t items-center'
                    >
                      {/* Agent */}
                      <div>
                        <Select
                          value={line.agent_id || '_none'}
                          onValueChange={value =>
                            handleAgentChange(
                              line.id,
                              value === '_none' ? '' : value
                            )
                          }
                        >
                          <SelectTrigger className='text-xs'>
                            <SelectValue placeholder='Select agent' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='_none'>Select agent</SelectItem>
                            {agents.map(agent => (
                              <SelectItem key={agent.id} value={agent.id}>
                                {agent.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Signal */}
                      <div>
                        <Select
                          value={line.signal_id || '_none'}
                          onValueChange={value =>
                            handleSignalChange(
                              line.id,
                              value === '_none' ? '' : value
                            )
                          }
                          disabled={
                            !line.agent_id || signalOptions.length === 0
                          }
                        >
                          <SelectTrigger className='text-xs'>
                            <SelectValue
                              placeholder={
                                line.agent_id
                                  ? signalOptions.length === 0
                                    ? 'No signals'
                                    : 'Select signal'
                                  : 'Select agent first'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='_none'>Select signal</SelectItem>
                            {signalOptions.map(s => (
                              <SelectItem
                                key={s.signal_id}
                                value={s.signal_id || ''}
                              >
                                {s.signal_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Type (auto) */}
                      <div className='text-xs text-muted-foreground px-1'>
                        {line.action_type || '-'}
                      </div>

                      {/* Qty */}
                      <div>
                        <Input
                          type='number'
                          min='1'
                          value={line.quantity}
                          onChange={e =>
                            updateLine(line.id, {
                              quantity:
                                Number.parseInt(e.target.value, 10) || 1,
                            })
                          }
                          className='text-xs'
                        />
                      </div>

                      {/* Price/Unit */}
                      <div>
                        <Input
                          type='number'
                          step='0.01'
                          min='0'
                          value={line.unit_price_dollars}
                          onChange={e =>
                            updateLine(line.id, {
                              unit_price_dollars:
                                Number.parseFloat(e.target.value) || 0,
                            })
                          }
                          className='text-xs'
                        />
                      </div>

                      {/* Total */}
                      <div className='text-sm font-medium'>
                        {formatCurrency(lineTotal)}
                      </div>

                      {/* Delete */}
                      <div className='w-8'>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          onClick={() => removeLine(line.id)}
                          disabled={lines.length === 1}
                        >
                          <Trash2 className='w-4 h-4' />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Summary */}
            <div className='bg-muted/50 p-4 rounded-lg space-y-3'>
              <div className='flex justify-between items-center text-sm'>
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <span>Discount:</span>
                <div className='flex items-center gap-1'>
                  <span className='text-muted-foreground'>$</span>
                  <Input
                    type='number'
                    step='0.01'
                    min='0'
                    value={discountDollars}
                    onChange={e => setDiscountDollars(e.target.value)}
                    className='w-24 h-7 text-xs'
                  />
                </div>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <div className='flex items-center gap-1'>
                  <span>Tax (</span>
                  <Input
                    type='number'
                    step='0.01'
                    min='0'
                    max='100'
                    value={taxRate}
                    onChange={e => setTaxRate(e.target.value)}
                    className='w-16 h-7 text-xs'
                  />
                  <span>%):</span>
                </div>
                <span>{formatCurrency(taxAmount)}</span>
              </div>
              <div className='flex justify-between font-medium border-t pt-2'>
                <span>Total:</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Notes */}
            <div className='space-y-2'>
              <Label htmlFor='notes'>Notes</Label>
              <Textarea
                id='notes'
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder='Optional notes or memo'
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className='border-t bg-background px-6 py-4 flex-shrink-0'>
          <div className='flex justify-end'>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
              className='gap-2'
            >
              {loading ? (
                <>Creating...</>
              ) : (
                <>
                  <Calculator className='w-4 h-4' />
                  Create Invoice
                </>
              )}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
